import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { FaWindows, FaApple, FaAndroid, FaAppStore, FaSpinner } from 'react-icons/fa';
import PageHeader from '../components/PageHeader';

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  padding: 2rem;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ContentContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Description = styled.div`
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
  padding: 2rem;
  border-radius: 15px;
  text-align: center;
  margin-bottom: 2rem;
  
  p {
    font-size: 1.2rem;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.9);
    max-width: 800px;
    margin: 0 auto;
    
    .red {
      color: #ff3333;
    }
  }
`;

const DownloadGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  overflow-x: auto;
  padding-bottom: 1rem;
  
  @media (max-width: 1400px) {
    grid-template-columns: repeat(4, minmax(250px, 1fr));
  }
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const DownloadCard = styled.div`
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(10px);
  border-radius: 15px;
  padding: 1.75rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  border: 2px solid rgba(255, 51, 51, 0.3);
  transition: transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
  min-width: 250px;
  height: 100%;
  justify-content: space-between;

  &:hover {
    transform: translateY(-5px);
    border-color: rgba(255, 51, 51, 0.6);
    box-shadow: 0 5px 15px rgba(255, 51, 51, 0.2);
  }
`;

const PlatformIcon = styled.div`
  font-size: 3rem;
  color: #ff3333;
  margin-bottom: 1rem;
`;

const PlatformName = styled.h3`
  font-size: 1.5rem;
  color: white;
  margin-bottom: 1rem;
`;

const DownloadButton = styled.a`
  background: rgba(255, 51, 51, 0.8);
  color: white;
  text-decoration: none;
  padding: 0.8rem 2rem;
  border-radius: 25px;
  font-size: 1rem;
  transition: background 0.3s ease, transform 0.3s ease;

  &:hover {
    background: rgba(255, 51, 51, 1);
    transform: translateY(-2px);
  }
`;

const Version = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  margin: 0.5rem 0 1rem;
`;

const LoadingSpinner = styled.div`
  color: rgba(255, 51, 51, 0.8);
  font-size: 1.5rem;
  margin: 0.5rem 0;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

function Download() {
  const [latestRelease, setLatestRelease] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLatestRelease = async () => {
      try {
        const response = await fetch('https://api.github.com/repos/nextcloud-releases/desktop/releases/latest');
        if (!response.ok) {
          throw new Error('Failed to fetch latest release');
        }
        const data = await response.json();
        
        // Find Windows and macOS assets
        const windowsAsset = data.assets.find(asset => asset.name.endsWith('-x64.msi'));
        const macOSAsset = data.assets.find(asset => asset.name.endsWith('.pkg'));

        setLatestRelease({
          version: data.tag_name.replace('v', ''),
          windowsUrl: windowsAsset?.browser_download_url,
          macOSUrl: macOSAsset?.browser_download_url
        });
      } catch (err) {
        console.error('Error fetching release:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchLatestRelease();
  }, []);

  const renderDownloadButton = (platform, url) => {
    if (loading) {
      return (
        <>
          <Version>Checking latest version...</Version>
          <LoadingSpinner>
            <FaSpinner />
          </LoadingSpinner>
        </>
      );
    }

    if (error || !url) {
      return (
        <>
          <Version>Temporarily unavailable</Version>
          <DownloadButton 
            as="span"
            style={{ opacity: 0.5, cursor: 'not-allowed' }}
          >
            Download
          </DownloadButton>
        </>
      );
    }

    return (
      <>
        <Version>Version {latestRelease.version}</Version>
        <DownloadButton 
          href={url}
          target="_blank"
          rel="noopener noreferrer"
        >
          Download
        </DownloadButton>
      </>
    );
  };

  return (
    <PageContainer>
      <ContentContainer>
        <PageHeader title="Download And Install" highlightWord="And" />
        <Description>
          <p>
            Access your Red<span className="red">Cloud</span> storage seamlessly across all your devices with the official Nextcloud-powered apps. 
            Whether you're on Windows, macOS, Android, or iOS, stay synchronized and secure with real-time file access, 
            automatic data backup, and instant file sharing capabilities.
          </p>
        </Description>

        <DownloadGrid>
          <DownloadCard>
            <PlatformIcon>
              <FaWindows />
            </PlatformIcon>
            <PlatformName>Windows</PlatformName>
            {renderDownloadButton('windows', latestRelease?.windowsUrl)}
          </DownloadCard>

          <DownloadCard>
            <PlatformIcon>
              <FaApple />
            </PlatformIcon>
            <PlatformName>macOS</PlatformName>
            {renderDownloadButton('macos', latestRelease?.macOSUrl)}
          </DownloadCard>

          <DownloadCard>
            <PlatformIcon>
              <FaAndroid />
            </PlatformIcon>
            <PlatformName>Android</PlatformName>
            <Version>Latest Version</Version>
            <DownloadButton 
              href="https://play.google.com/store/apps/details?id=com.nextcloud.client"
              target="_blank"
              rel="noopener noreferrer"
            >
              Get on Play Store
            </DownloadButton>
          </DownloadCard>

          <DownloadCard>
            <PlatformIcon>
              <FaAppStore />
            </PlatformIcon>
            <PlatformName>iOS</PlatformName>
            <Version>Latest Version</Version>
            <DownloadButton 
              href="https://itunes.apple.com/us/app/nextcloud/id1125420102?mt=8"
              target="_blank"
              rel="noopener noreferrer"
            >
              Get on App Store
            </DownloadButton>
          </DownloadCard>
        </DownloadGrid>
      </ContentContainer>
    </PageContainer>
  );
}

export default Download;
